import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { timeCapsuleApi, ZoneStatusData, ZoneStatusResponse, ZonePayloadData, ApiError } from '../../services/apiService';

// Zone definitions based on the exhibit mapping
export const ZONE_IDS = {
    ORANG_LAUT: '4.1', // ✅ Confirmed implemented - Orang Laut exhibit
    MARKET_TRADERS: '4.2', // ✅ Market Traders exhibit
    // Note: Other zones may not be implemented yet on the server side
    // Uncomment these as they become available:
    BADANG: '4.4', // Badang exhibit  
    BOOK_OF_MYTHS: '5.0', // Book of Myths exhibit
    // SG60: '7.0', // SG60 exhibit
    PERANAKAN_DESIGN: '10.3', // Peranakan Design exhibit
    TIME_CAPSULE: '15.7', // Time Capsule exhibit
} as const;

export type ZoneId = typeof ZONE_IDS[keyof typeof ZONE_IDS];

export interface ZoneState {
    zones: Record<string, ZoneStatusResponse>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
    claimingZone: string | null;
    lastChecked: Record<string, number>;
}

const initialState: ZoneState = {
    zones: {},
    loading: {},
    error: {},
    claimingZone: null,
    lastChecked: {},
};

// Async thunks for zones API
export const getZoneStatus = createAsyncThunk<
    ZoneStatusResponse,
    { zoneId: string; visitorId?: string },
    { rejectValue: string }
>(
    'zones/getZoneStatus',
    async ({ zoneId, visitorId }, { rejectWithValue }) => {
        try {
            console.log(`🔍 Fetching zone status for: ${zoneId}`, { 
                hasVisitorId: !!visitorId,
                visitorIdPreview: visitorId ? `${visitorId.substring(0, 8)}...` : 'none'
            });
            const response = await timeCapsuleApi.getZoneStatus(zoneId, visitorId);
            console.log(`✅ Zone status fetched for ${zoneId}:`, response);
            return response;
        } catch (error) {
            const apiError = error as ApiError;
            console.error(`❌ Failed to fetch zone status for ${zoneId}:`, apiError);
            return rejectWithValue(apiError.message || 'Failed to fetch zone status');
        }
    }
);

export const updateZoneStatus = createAsyncThunk<
    ZoneStatusResponse,
    { zoneId: string; statusData: ZoneStatusData; visitorId?: string },
    { rejectValue: string }
>(
    'zones/updateZoneStatus',
    async ({ zoneId, statusData, visitorId }, { rejectWithValue }) => {
        try {
            console.log(`🔄 Updating zone status for: ${zoneId}`, statusData);
            const response = await timeCapsuleApi.updateZoneStatus(zoneId, statusData, visitorId);
            console.log(`✅ Zone status updated for ${zoneId}:`, response);
            return response;
        } catch (error) {
            const apiError = error as ApiError;
            console.error(`❌ Failed to update zone status for ${zoneId}:`, apiError);
            return rejectWithValue(apiError.message || 'Failed to update zone status');
        }
    }
);

export const claimZoneStatus = createAsyncThunk<
    ZoneStatusResponse,
    { zoneId: string; visitorId?: string },
    { rejectValue: string }
>(
    'zones/claimZoneStatus',
    async ({ zoneId, visitorId }, { rejectWithValue }) => {
        try {
            console.log(`🎯 Claiming zone: ${zoneId}`, { 
                hasVisitorId: !!visitorId,
                visitorIdPreview: visitorId ? `${visitorId.substring(0, 8)}...` : 'none'
            });
            const response = await timeCapsuleApi.claimZoneStatus(zoneId, visitorId);
            console.log(`✅ Zone claimed successfully: ${zoneId}`, response);
            return response;
        } catch (error) {
            const apiError = error as ApiError;
            console.error(`❌ Failed to claim zone ${zoneId}:`, apiError);
            return rejectWithValue(apiError.message || 'Failed to claim zone');
        }
    }
);

export const updateZonePayload = createAsyncThunk<
    ZoneStatusResponse,
    { zoneId: string; payloadData: ZonePayloadData; visitorId?: string },
    { rejectValue: string }
>(
    'zones/updateZonePayload',
    async ({ zoneId, payloadData, visitorId }, { rejectWithValue }) => {
        try {
            console.log(`📦 Updating zone payload for: ${zoneId}`, payloadData);
            const response = await timeCapsuleApi.updateZonePayload(zoneId, payloadData, visitorId);
            console.log(`✅ Zone payload updated for ${zoneId}:`, response);
            return response;
        } catch (error) {
            const apiError = error as ApiError;
            console.error(`❌ Failed to update zone payload for ${zoneId}:`, apiError);
            return rejectWithValue(apiError.message || 'Failed to update zone payload');
        }
    }
);

const zonesSlice = createSlice({
    name: 'zones',
    initialState,
    reducers: {
        clearZoneError: (state, action: PayloadAction<string>) => {
            const zoneId = action.payload;
            state.error[zoneId] = null;
        },
        clearAllZoneErrors: (state) => {
            state.error = {};
        },
        setZoneLastChecked: (state, action: PayloadAction<{ zoneId: string; timestamp: number }>) => {
            const { zoneId, timestamp } = action.payload;
            state.lastChecked[zoneId] = timestamp;
        },
    },
    extraReducers: (builder) => {
        // Get zone status
        builder.addCase(getZoneStatus.pending, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            state.loading[zoneId] = true;
            state.error[zoneId] = null;
        });
        builder.addCase(getZoneStatus.fulfilled, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            const response = action.payload;
            state.loading[zoneId] = false;
            state.zones[zoneId] = response;
            state.lastChecked[zoneId] = Date.now();
        });
        builder.addCase(getZoneStatus.rejected, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            state.loading[zoneId] = false;
            state.error[zoneId] = action.payload || 'Unknown error';
        });

        // Update zone status
        builder.addCase(updateZoneStatus.pending, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            state.loading[zoneId] = true;
            state.error[zoneId] = null;
        });
        builder.addCase(updateZoneStatus.fulfilled, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            const response = action.payload;
            state.loading[zoneId] = false;
            state.zones[zoneId] = response;
            state.lastChecked[zoneId] = Date.now();
        });
        builder.addCase(updateZoneStatus.rejected, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            state.loading[zoneId] = false;
            state.error[zoneId] = action.payload || 'Unknown error';
        });

        // Claim zone status
        builder.addCase(claimZoneStatus.pending, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            state.loading[zoneId] = true;
            state.error[zoneId] = null;
            state.claimingZone = zoneId;
        });
        builder.addCase(claimZoneStatus.fulfilled, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            const response = action.payload;
            state.loading[zoneId] = false;
            state.zones[zoneId] = response;
            state.claimingZone = null;
            state.lastChecked[zoneId] = Date.now();
        });
        builder.addCase(claimZoneStatus.rejected, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            state.loading[zoneId] = false;
            state.error[zoneId] = action.payload || 'Unknown error';
            state.claimingZone = null;
        });

        // Update zone payload
        builder.addCase(updateZonePayload.pending, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            state.loading[zoneId] = true;
            state.error[zoneId] = null;
        });
        builder.addCase(updateZonePayload.fulfilled, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            const response = action.payload;
            const previousStatus = state.zones[zoneId];
            
            console.log('🔍 Redux updateZonePayload.fulfilled:', {
                zoneId,
                previousStatus: previousStatus?.status,
                previousClaimedBy: previousStatus?.claimedBy,
                apiResponse: {
                    status: response.status,
                    claimedBy: response.claimedBy,
                    payload: response.payload
                },
                payloadData: action.meta.arg.payloadData
            });
            
            // Check if API is incorrectly returning 'available' status when it should be 'processing'
            if (previousStatus?.status === 'processing' && response.status === 'available' && response.claimedBy) {
                console.warn('⚠️ API returned available status but zone is still claimed - preserving processing status');
                // Preserve the processing status but update the payload
                state.zones[zoneId] = {
                    ...response,
                    status: 'processing' // Override API status to maintain processing state
                };
            } else {
                state.zones[zoneId] = response;
            }
            
            state.loading[zoneId] = false;
            state.lastChecked[zoneId] = Date.now();
        });
        builder.addCase(updateZonePayload.rejected, (state, action) => {
            const zoneId = action.meta.arg.zoneId;
            state.loading[zoneId] = false;
            state.error[zoneId] = action.payload || 'Unknown error';
        });
    },
});

export const { clearZoneError, clearAllZoneErrors, setZoneLastChecked } = zonesSlice.actions;
export default zonesSlice.reducer; 